import { useCallback, useState } from 'react';

import type { ActionFunctionArgs, MetaFunction } from '@remix-run/cloudflare';
import { Navigation } from '../components/navigation';
import { images } from '../constants/images';
import { json } from '@remix-run/react';
import { retry } from '../retry';
import { ApplicationEnvironment } from '../environment';
import { KumbhSignificanceSection } from '../sections/kumbh-significance';
import { AccommodationAndPackagesSection } from '../sections/accommodation-and-packages';
import { Kumbh2019Section } from '../sections/kumbh-2019';
import { SanskritiVanSection } from '../sections/sanskriti-van';
import { HavenAwayFromHomeSection } from '../sections/haven-away-from-home';
import { KumbhSukritHeader } from '../components/kumbh-sukrit-header';
import { VideoModal } from '../components/video-modal';
import { Footer } from '../components/footer';
import { getOrigin } from '../get-origin';
import { propagateError } from '../propagate-error';

export const meta: MetaFunction = () => {
  return [
    { title: 'Kumbh Sukrit — Magh Mela 2026' },
    {
      name: 'description',
      content:
        'Experience Magh Mela 2026 at Sanskriti Vann — Premium tent & mudhouse accommodations in Prayagraj',
    },
  ];
};

export const certifications = [
  {
    src: images['ycb'],
    alt: 'Certified by the Yoga Certification Board of Ministry of Ayush',
    title: 'Yoga Certification Board, Ministry of Ayush',
    className: 'max-h-16',
  },
  {
    src: images['mot-medal'],
    alt: 'Certified as an Inbound Tour Operator by the Ministry of Tourism India',
    title: 'Inbound Tour Operator, India',
    className: 'max-h-16',
  },
  {
    src: images['incin-logo'],
    alt: 'Certified under Incredible India',
    title: 'Incredible India',
    className: 'max-h-10',
  },
  {
    src: images['icc'],
    title: 'Indian Chamber of Commerce',
    alt: 'Certified by the Indian Chamber of Commerce',
    className: 'max-h-16',
  },
  {
    src: images['iato'],
    alt: 'Certified by the Indian Association of Tour Operators',
    title: 'Indian Association of Tour Operators, India',
    className: 'max-h-16',
  },
  {
    src: images['mot-govin'],
    alt: 'Certified by the Ministry of Tourism India',
    title: 'Ministry of Tourism, Government of India',
    className: 'max-h-16',
  },
];

export const action = async (args: ActionFunctionArgs) => {
  const request = args.request;
  const origin = getOrigin(request);
  const cookie = request.headers.get('cookie') ?? '';
  const environment = args.context.cloudflare.env as ApplicationEnvironment;

  const apiUrl = environment.API_URL;
  const formData = await request.formData();

  // Extract structured inquiry data
  const email = formData.get('email')?.toString() ?? '';
  const name = formData.get('name')?.toString() ?? '';
  const phone = formData.get('phone')?.toString() ?? '';

  // Structured fields
  const tentTypesRaw = formData.get('accommodationTypes')?.toString();
  const supplementaryItemsRaw = formData.get('supplementaryItems')?.toString();
  const supplementaryItems = supplementaryItemsRaw
    ? JSON.parse(supplementaryItemsRaw)
    : [];
  const tentTypes = tentTypesRaw ? JSON.parse(tentTypesRaw) : [];
  const checkIn = formData.get('checkIn')?.toString() || undefined;
  const checkOut = formData.get('checkOut')?.toString() || undefined;
  const guestsRaw = formData.get('guests')?.toString();
  const guests = guestsRaw ? parseInt(guestsRaw, 10) : undefined;
  const message = formData.get('message')?.toString() || undefined;

  try {
    const response = await retry('post-inquiry', (signal) =>
      fetch(`${apiUrl}/api/inquiry`, {
        body: JSON.stringify({
          email,
          name,
          phone,
          tentTypes,
          supplementaryItems,
          checkIn,
          checkOut,
          guests,
          message,
        }),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          origin,
          cookie,
        },
        signal,
      }),
    );

    if (!response.ok) {
      throw Error('Error: inquiry failed');
    }

    const data = (await response.json()) as { inquiryId: string };
    return json({ success: true, inquiryId: data.inquiryId });
  } catch (e) {
    propagateError(e, 'Inquiry submission failed');
  }

  return json({ success: false });
};

export default function Index() {
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const onClick = useCallback((src: string) => {
    setIsOpen(true);
    setVideoSrc(src);
  }, []);
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className="w-full bg-green-100">
      <Navigation />
      <KumbhSukritHeader />
      <main className="w-full">
        <VideoModal videoSrc={videoSrc} isOpen={isOpen} onClose={onClose} />
        <HavenAwayFromHomeSection />
        <SanskritiVanSection />
        <AccommodationAndPackagesSection />
        <Kumbh2019Section />
        <KumbhSignificanceSection />
      </main>
      <Footer />
    </div>
  );
}
